/**
 * <ImageUploadField> — combined URL input + upload button.
 *
 * Lets the operator either:
 *   • Paste an https://… URL directly, OR
 *   • Click 📤 to pick a file from disk — the file is POST-uploaded to
 *     `/api/admin/upload-image`, and the returned permanent URL is written
 *     back into the same field via `onChange`.
 *
 * Used by the banner / hero / offer management UI so the admin doesn't
 * have to host images elsewhere first.
 */
import React, { useRef, useState } from "react";
import { Upload, Loader2, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { resolveAssetUrl } from "@/lib/assetUrl";

export default function ImageUploadField({ label, value, onChange, apiFetch, testid }) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const handlePick = async (file) => {
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      toast.error("Please pick an image");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await apiFetch("/admin/upload-image", { method: "POST", body: fd, headers: undefined });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Upload failed");
      const url = data?.url || data?.image_url || "";
      if (!url) throw new Error("No URL returned");
      onChange(url);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(e?.message || "Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const preview = value && resolveAssetUrl(value);

  return (
    <label className="block" data-testid={testid}>
      <span className="block text-[10px] uppercase tracking-widest text-white/40 mb-1">{label}</span>
      <div className="flex items-stretch gap-2">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div className="w-12 h-12 rounded-lg overflow-hidden bg-black/40 border border-white/10 shrink-0 flex items-center justify-center text-white/30">
            {preview ? <img src={preview} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-4 h-4" />}
          </div>
          <input
            type="text"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="https://… or click upload"
            className="flex-1 h-12 px-3 rounded-lg bg-black/40 border border-white/10 text-xs"
            data-testid={testid ? `${testid}-input` : undefined}
          />
        </div>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="h-12 px-3 rounded-lg bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 text-xs font-bold inline-flex items-center gap-1.5 disabled:opacity-50"
          data-testid={testid ? `${testid}-upload` : undefined}
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          <span className="hidden sm:inline">Upload</span>
        </button>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => handlePick(e.target.files?.[0])}
      />
    </label>
  );
}
